import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BotTag } from '../../../../../../utils/Tags'
import { ButtonLink } from '../../../../../../utils/UI'
import { useTw } from '../../../../../../utils/Styles'
import { ChevronRightIcon } from '@heroicons/react/outline'
import { useProfile } from '../../../../../../utils/Profile'
import { ConversationsQuery } from '../Conversation.generated'
import { MessagePreview, useSenderInfo } from '../../../../../../utils'
import { formatDistanceToNowShort } from '../../../../../../utils/formatDistanceShort'

type Props = {
  conversation: ConversationsQuery['Conversations'][0]
  showMore: boolean
}

function Ping() {
  const tw = useTw()
  return (
    <span className={tw`flex absolute -top-1.5 -right-1.5 h-3 w-3`}>
      <span
        className={tw`animate-ping absolute inline-flex h-full w-full rounded-full bg-foreground opacity-75`}
      ></span>
      <span
        className={tw`relative inline-flex rounded-full h-3 w-3 bg-foreground`}
      ></span>
    </span>
  )
}

export function ConversationMessage({ conversation, showMore }: Props) {
  const tw = useTw()
  const profile = useProfile()
  const [t] = useTranslation('common')
  const navigate = useNavigate()
  const highlight =
    conversation?.lastItem?.senderType === 'operator' &&
    conversation.answered === false

  const sender = useSenderInfo(
    conversation.lastItem || { sender: profile.visitor }
  )

  return (
    <div className={tw`relative divide-y divide-neutral-300 w-full`}>
      <button
        className={tw`items-center justify-between flex cursor-pointer space-x-2 p-4 w-inherit`}
        onClick={() => navigate(`/conversation/${conversation._id}`)}
      >
        <div className={tw`flex-grow flex items-center w-inherit`}>
          {highlight ? <Ping /> : null}
          <img
            className={tw`select-none inline-block h-12 w-12 object-cover flex-shrink-0 rounded-full`}
            alt={`${sender.name} Avatar`}
            src={sender.logoURL}
          />
          <div className={tw`pl-2 flex flex-grow flex-col overflow-hidden`}>
            <div className={tw`flex items-center justify-between truncate`}>
              <p className={tw`font-bold text-gray-900`}>
                {sender.name}
                {sender?.bot ? <BotTag /> : null}
              </p>
              <p className={tw`text-xs uppercase`}>
                {formatDistanceToNowShort(
                  DateTime.fromISO(
                    conversation?.lastItem?.createdAt || conversation.createdAt
                  )
                )}
              </p>
            </div>
            {conversation?.lastItem?.content?.messageString ? (
              <p
                title={conversation.lastItem.content.messageString}
                className={tw`flex whitespace-nowrap ${
                  highlight ? 'text-gray-900 font-semibold' : 'text-gray-600'
                }`}
              >
                <MessagePreview message={conversation.lastItem} />
              </p>
            ) : null}
          </div>
        </div>
        <ChevronRightIcon
          className={tw`h-8 w-8 right-1 top-2.5 text-foreground`}
        />
      </button>
      {showMore ? (
        <div className={tw`w-full py-2 flex items-center justify-center`}>
          <ButtonLink
            onClick={() => navigate('/conversations')}
            text={t('home.conversation.existing.previous_convo')}
          />
        </div>
      ) : null}
    </div>
  )
}
